import { type ReactNode, useEffect, useId, useState } from 'react';
import clsx from 'clsx';
import { USD_PER_CREDIT } from '@flintmint/core-shared/constants';
import { APP_SETTINGS_RANGES, isValidSettingValue, pluralize, SYSTEM_SETTINGS_RANGES } from '@flintmint/core-shared/utils';
import { Button } from '@flintmint/web-shared/components/button';
import { Modal } from '@flintmint/web-shared/components/modal';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { usePageTitle } from '@/hooks/use-page-title';
import type { BillingSettings } from '@/services/settings-service';
import { formatNumber, formatUsd } from '@/utils/format-values';
import { useBillingSettings } from './use-billing-settings';

import checkIconUrl from './check.svg';
import styles from './settings-page.module.scss';

export const SettingsPage = () => {
  usePageTitle('Settings');
  const { settings, unsavedKeys, isLoading, error, save } = useBillingSettings();

  if (isLoading) return <SettingsPageSkeleton />;

  return (
    <div className={styles.page}>
      <SettingsHeader />
      {error && <p className={styles.error}>{error}</p>}
      {settings && <SettingsForm settings={settings} unsavedKeys={unsavedKeys} save={save} />}
    </div>
  );
};

export const SettingsPageSkeleton = () => (
  <div className={styles.page}>
    <SettingsHeader />
    <div className={styles.cards}>
      {SETTING_CARDS.map((card) => (
        <SettingCard key={card.title} card={card}>
          {SETTING_FIELDS.filter((field) => field.card === card.title).map((field) => (
            <div key={field.key} className={styles.field} data-testid="settings-field-skeleton">
              <span className={styles.label}>{field.label}</span>
              <Skeleton width={240} height={36} />
            </div>
          ))}
        </SettingCard>
      ))}
    </div>
  </div>
);

const SettingsHeader = () => <h2 className={styles.title}>Settings</h2>;

// Cards fill the grid in this order; a two-column card spans two grid columns.
const SETTING_CARDS = [
  { title: 'Free' },
  { title: 'Past due' },
  { title: 'Warnings' },
  { title: 'Trial', twoColumns: true },
  { title: 'Estimates' },
] as const satisfies readonly { title: string; twoColumns?: boolean }[];

type SettingCardConfig = (typeof SETTING_CARDS)[number];

const SettingCard = ({ card, children }: { card: SettingCardConfig; children: ReactNode }) => (
  <section className={clsx(styles.card, { [styles.twoColumnCard]: 'twoColumns' in card && card.twoColumns })}>
    <h3 className={styles.sectionTitle}>{card.title}</h3>
    {children}
  </section>
);

interface SettingFieldConfig {
  key: keyof BillingSettings;
  card: SettingCardConfig['title'];
  label: string;
  // Drawn inside the input; a Joules field also shows its USD equivalent.
  unit: 'Joules' | '%' | 'days';
  range: readonly [min: number, max: number];
  helper: string;
  // Shown under the change in the save confirmation.
  changeNote: string;
}

const SETTING_FIELDS: readonly SettingFieldConfig[] = [
  {
    key: 'aiPreviewCredits',
    card: 'Free',
    label: 'AI Preview quota',
    unit: 'Joules',
    range: SYSTEM_SETTINGS_RANGES.aiPreviewCredits,
    helper: 'Applies to users who accept the agreements after you save. Existing users keep their current quota.',
    changeNote: 'Applies to users who accept the agreements after you save.',
  },
  {
    key: 'trialDays',
    card: 'Trial',
    label: 'Trial length',
    unit: 'days',
    range: SYSTEM_SETTINGS_RANGES.trialDays,
    helper: 'Applies to subscriptions that start after you save.',
    changeNote: 'Applies to subscriptions that start after you save.',
  },
  {
    key: 'trialCredits',
    card: 'Trial',
    label: 'Trial quota',
    unit: 'Joules',
    range: SYSTEM_SETTINGS_RANGES.trialCredits,
    helper: 'Applies to Trials that start after you save.',
    changeNote: 'Applies to Trials that start after you save.',
  },
  {
    key: 'gracePeriodQuotaPercent',
    card: 'Past due',
    label: 'Grace quota',
    unit: '%',
    range: SYSTEM_SETTINGS_RANGES.gracePeriodQuotaPercent,
    helper: 'Share of the monthly quota a subscriber can use while a payment is past due. Applies immediately.',
    changeNote: 'Applies immediately to subscribers whose payment is past due.',
  },
  {
    key: 'quotaPercentUsedWarning',
    card: 'Warnings',
    label: 'Quota warning threshold',
    unit: '%',
    range: APP_SETTINGS_RANGES.quotaPercentUsedWarning,
    helper: 'Users see a warning that their quota is running out once they have used this share of it. Applies immediately.',
    changeNote: 'Applies immediately.',
  },
  {
    key: 'creditsPerTypicalNote',
    card: 'Estimates',
    label: 'Joules per typical note',
    unit: 'Joules',
    range: APP_SETTINGS_RANGES.creditsPerTypicalNote,
    helper: 'Users see Joule amounts as typical notes: Joules ÷ this number, rounded down. Applies immediately.',
    changeNote: 'Applies immediately.',
  },
];

const SAVED_NOTICE_MS = 3000;

const SettingsForm = ({
  settings,
  unsavedKeys,
  save,
}: {
  settings: BillingSettings;
  // Keys whose stored value is missing or invalid: the shown default counts as a change so saving it repairs the doc.
  unsavedKeys: (keyof BillingSettings)[];
  save: (changes: Partial<BillingSettings>) => Promise<void>;
}) => {
  const [drafts, setDrafts] = useState(() => draftsOf(settings));
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { showToastNotification } = useToaster();

  useEffect(() => {
    if (!isSaved) return;
    const timeoutId = setTimeout(() => setIsSaved(false), SAVED_NOTICE_MS);
    return () => clearTimeout(timeoutId);
  }, [isSaved]);

  const parsedValues = Object.fromEntries(
    SETTING_FIELDS.map((field) => [field.key, parseSettingInput(drafts[field.key], field.range)])
  ) as Record<keyof BillingSettings, number | null>;
  const errorCount = Object.values(parsedValues).filter((value) => value === null).length;
  const hasDraftChanges = SETTING_FIELDS.some(({ key }) => drafts[key] !== String(settings[key]));
  const changes: Partial<BillingSettings> = {};
  let editCount = 0;
  let notSavedCount = 0;
  for (const { key } of SETTING_FIELDS) {
    const value = parsedValues[key];
    const isEdited = value !== null && value !== settings[key];
    if (value === null || (!isEdited && !unsavedKeys.includes(key))) continue;
    changes[key] = value;
    if (isEdited) editCount++;
    else notSavedCount++;
  }

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await save(changes);
      setIsSaved(true);
      setIsConfirming(false);
    } catch {
      showToastNotification({ type: 'error', title: 'Failed to save settings' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className={styles.cards}>
        {SETTING_CARDS.map((card) => (
          <SettingCard key={card.title} card={card}>
            {SETTING_FIELDS.filter((field) => field.card === card.title).map((field) => (
              <SettingField
                key={field.key}
                field={field}
                value={drafts[field.key]}
                parsedValue={parsedValues[field.key]}
                isChanged={field.key in changes || parsedValues[field.key] === null}
                disabled={isSaving}
                onChange={(value) => {
                  setDrafts((current) => ({ ...current, [field.key]: value }));
                  setIsSaved(false);
                }}
              />
            ))}
          </SettingCard>
        ))}
      </div>
      {(editCount > 0 || notSavedCount > 0 || errorCount > 0 || isSaved) && (
        <SaveBar
          editCount={editCount}
          hasDraftChanges={hasDraftChanges}
          notSavedCount={notSavedCount}
          errorCount={errorCount}
          isSaved={isSaved}
          isSaving={isSaving}
          onDiscard={() => setDrafts(draftsOf(settings))}
          onSave={() => setIsConfirming(true)}
        />
      )}
      {isConfirming && (
        <ConfirmChangesDialog
          changes={changes}
          settings={settings}
          unsavedKeys={unsavedKeys}
          isSaving={isSaving}
          onCancel={() => !isSaving && setIsConfirming(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

/** Each field's saved value as the text its input starts from. */
const draftsOf = (settings: BillingSettings) =>
  Object.fromEntries(SETTING_FIELDS.map((field) => [field.key, String(settings[field.key])])) as Record<
    keyof BillingSettings,
    string
  >;

const SettingField = ({
  field,
  value,
  parsedValue,
  isChanged,
  disabled,
  onChange,
}: {
  field: SettingFieldConfig;
  value: string;
  // The draft as a valid setting value, or null while it is invalid.
  parsedValue: number | null;
  isChanged: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) => {
  const inputId = useId();
  const errorId = useId();
  const isInvalid = parsedValue === null;

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label htmlFor={inputId} className={styles.label}>
          {field.label}
        </label>
        {isChanged && (
          <span className={styles.changedDot}>
            <span className={styles.visuallyHidden}>(changed)</span>
          </span>
        )}
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputHolder}>
          <input
            id={inputId}
            className={styles.input}
            inputMode="numeric"
            value={value}
            disabled={disabled}
            aria-invalid={isInvalid}
            aria-describedby={isInvalid ? errorId : undefined}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className={styles.suffix}>{field.unit}</span>
        </div>
        {field.unit === 'Joules' && parsedValue !== null && (
          <span className={styles.usdEquivalent}>≈ {formatUsd(parsedValue * USD_PER_CREDIT)} of AI usage</span>
        )}
      </div>
      {isInvalid && (
        <p id={errorId} className={styles.fieldError}>
          {rangeError(field.range)}
        </p>
      )}
      <p className={styles.helper}>{field.helper}</p>
    </div>
  );
};

const SaveBar = ({
  editCount,
  hasDraftChanges,
  notSavedCount,
  errorCount,
  isSaved,
  isSaving,
  onDiscard,
  onSave,
}: {
  editCount: number;
  // Any draft text differs from its saved value, valid or not.
  hasDraftChanges: boolean;
  // Never-saved keys left unedited: they can be saved but there is nothing to discard.
  notSavedCount: number;
  errorCount: number;
  isSaved: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) => (
  <div className={styles.saveBar}>
    {isSaved ? (
      <span className={styles.saved}>
        <img src={checkIconUrl} width={14} height={14} alt="" />
        Changes saved
      </span>
    ) : (
      <span className={styles.saveBarStatus}>{saveBarStatus(editCount, notSavedCount, errorCount)}</span>
    )}
    <div className={styles.saveBarButtons}>
      {!isSaved && hasDraftChanges && (
        <Button color="light" appearance="outline" size="medium" disabled={isSaving} onClick={onDiscard}>
          Discard
        </Button>
      )}
      <Button
        color="hero"
        size="medium"
        disabled={isSaving || errorCount > 0 || editCount + notSavedCount === 0}
        onClick={onSave}
      >
        Save changes
      </Button>
    </div>
  </div>
);

/** The save bar's status: errors first, then edits, then never-saved settings. */
const saveBarStatus = (editCount: number, notSavedCount: number, errorCount: number) => {
  if (errorCount > 0) return `Fix ${pluralize(errorCount, 'error')} to save`;
  if (editCount > 0) {
    const edits = pluralize(editCount, 'unsaved change');
    return notSavedCount > 0 ? `${edits}, ${notSavedCount} not saved yet` : edits;
  }
  return `${pluralize(notSavedCount, 'setting')} not saved yet`;
};

const ConfirmChangesDialog = ({
  changes,
  settings,
  unsavedKeys,
  isSaving,
  onCancel,
  onConfirm,
}: {
  changes: Partial<BillingSettings>;
  settings: BillingSettings;
  unsavedKeys: (keyof BillingSettings)[];
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <Modal
    open
    maxWidth={480}
    onClose={onCancel}
    title={`Save ${pluralize(Object.keys(changes).length, 'change')}?`}
    primaryButton={{ children: 'Save changes', color: 'hero', disabled: isSaving, onClick: onConfirm }}
    secondaryButton={{ children: 'Cancel', onClick: onCancel, disabled: isSaving }}
    data-testid="settings-confirm-dialog"
  >
    <ul className={styles.changeList}>
      {SETTING_FIELDS.filter((field) => field.key in changes).map((field) => (
        <li key={field.key} data-testid="settings-change">
          <p className={styles.changeValues}>
            <strong>{field.label}</strong>{' '}
            {unsavedKeys.includes(field.key) ? 'not saved yet' : withUnit(settings[field.key], field.unit, false)} →{' '}
            {withUnit(changes[field.key]!, field.unit, true)}
          </p>
          <p className={styles.changeNote}>{field.changeNote}</p>
        </li>
      ))}
    </ul>
  </Modal>
);

/** A setting value as the confirmation reads it: a percent always carries its sign, a word unit only when `withWordUnit`. */
const withUnit = (value: number, unit: SettingFieldConfig['unit'], withWordUnit: boolean) => {
  const number = formatNumber(value, { fractionDigits: 0 });
  if (unit === '%') return `${number}%`;
  return withWordUnit ? `${number} ${unit}` : number;
};

/** The whole number typed in `raw` when it lies within `range`, else null. */
const parseSettingInput = (raw: string, range: readonly [min: number, max: number]): number | null => {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return isValidSettingValue(value, range) ? value : null;
};

/** The error shown for a draft outside `range`. */
const rangeError = ([min, max]: readonly [min: number, max: number]) =>
  Number.isFinite(max) ? `Enter a whole number from ${min} to ${max}.` : `Enter a whole number of at least ${min}.`;
